/* eslint-disable react/prop-types */
import { FC } from "react";
import DOMPurify from "dompurify";
import { Loader2 } from "lucide-react";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";
import { ContentBlock } from "../ContentBlocksEditor/ContentBlocksEditor";

interface Author {
	firstName: string;
	lastName: string;
}

interface Props {
	title: string;
	/** Azure blob reference for the hero image (already uploaded). */
	heroImageRef: string | null;
	/**
	 * Ephemeral blob URL for a hero image that's mid-upload. Preferred
	 * over the reference so the preview doesn't flash while AzureImage
	 * resolves the SAS URL.
	 */
	heroImagePreviewUrl?: string | null;
	blocks: ContentBlock[];
	author?: Author | null;
	/** Optional caption shown above the title (e.g. "Sent 20 Apr 2026"). */
	dateLabel?: string | null;
}

const TEXT_ALLOWED_TAGS = [
	"p",
	"br",
	"strong",
	"em",
	"u",
	"a",
	"ul",
	"ol",
	"li",
	"h1",
	"h2",
	"h3",
	"blockquote",
	"span"
];

const TEXT_ALLOWED_ATTR = ["href", "target", "rel", "class"];

// Hardens any surviving `<a target="_blank">` by forcing
// rel="noopener noreferrer". Without this, an admin can author a
// target="_blank" link (or paste HTML that contains one) without
// rel, and the opened page can navigate back via window.opener —
// reverse-tabnabbing. The hook runs once at module load. Only
// adds the hook once even if the module is re-imported in
// development.
let purifyHardeningAttached = false;
const attachTargetBlankHardening = () => {
	if (purifyHardeningAttached) return;
	purifyHardeningAttached = true;
	DOMPurify.addHook("afterSanitizeAttributes", (node) => {
		if (node.nodeName === "A" && node.getAttribute("target") === "_blank") {
			node.setAttribute("rel", "noopener noreferrer");
		}
	});
};
attachTargetBlankHardening();

// Strict allow-list sanitisation — no img/video/iframe/script, no
// event handlers. Inline media blocks go through the typed image /
// video renderers below instead of text-block HTML.
const sanitiseTextBlock = (raw: string): string =>
	DOMPurify.sanitize(raw, {
		ALLOWED_TAGS: TEXT_ALLOWED_TAGS,
		ALLOWED_ATTR: TEXT_ALLOWED_ATTR
	});

const TextBlock: FC<{ content: string }> = ({ content }) => {
	// DOMPurify strips every tag / attribute not in our allow-list.
	// The sanitised string is then the only thing passed to
	// dangerouslySetInnerHTML — same safety posture as the sent view on
	// EditAnnouncement, scoped here to the text-block allow-list.
	const __html = sanitiseTextBlock(content);
	return (
		<div
			className="prose prose-sm max-w-none text-foreground"
			dangerouslySetInnerHTML={{ __html }}
		/>
	);
};

const ImageBlockPreview: FC<{ block: ContentBlock }> = ({ block }) => {
	// Prefer the blob preview when the file has just been picked so the
	// image renders instantly; fall back to AzureImage for the persisted
	// reference (which resolves to a SAS URL).
	// Prefer author-supplied alt text. Explicit empty string = the
	// author marked the image as decorative (valid). Undefined falls
	// back to a generic label so screen-reader users still get *some*
	// indication of the image's presence.
	const altText = block.alt ?? "Announcement content";

	// w-full + h-auto = render at the image's natural aspect ratio,
	// scaled to fill the preview card's width. No object-fit cropping
	// and no max-height so tall and wide images both display at the
	// ratio they were uploaded in — the same rule the mobile app
	// follows when it actually renders the announcement.
	if (block.previewUrl) {
		return (
			<div className="rounded-lg overflow-hidden my-3">
				<img src={block.previewUrl} alt={altText} className="w-full h-auto" />
			</div>
		);
	}
	if (block.content) {
		return (
			<div className="rounded-lg overflow-hidden my-3">
				<AzureImage
					uuid={block.content}
					imageProfile="admin-content-image"
					alt={altText}
					className="w-full h-auto"
				/>
			</div>
		);
	}
	return null;
};

const VideoBlockPreview: FC<{ block: ContentBlock }> = ({ block }) => {
	const { url, processingStatus } = useAzureVideoDownload(
		"video",
		undefined,
		block.content || undefined
	);

	// aria-label gives the <video> an accessible name for screen
	// readers. Falls back to a generic label; authors can override via
	// the block's `alt` field once the editor UI exposes it.
	const videoLabel = block.alt ?? "Announcement video";

	if (block.previewUrl) {
		return (
			<div className="rounded-lg overflow-hidden my-3 bg-black">
				<video
					controls
					src={block.previewUrl}
					aria-label={videoLabel}
					className="w-full max-h-[400px]"
				/>
				{/* eslint-disable-line */}
			</div>
		);
	}
	if (!block.content) return null;
	if (url) {
		return (
			<div className="rounded-lg overflow-hidden my-3 bg-black">
				<video
					controls
					src={url}
					aria-label={videoLabel}
					className="w-full max-h-[400px]"
				/>
				{/* eslint-disable-line */}
			</div>
		);
	}
	return (
		<div
			role="status"
			aria-live="polite"
			className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground my-3 rounded-lg border border-border bg-subtle"
		>
			<Loader2 className="h-4 w-4 animate-spin" />
			{processingStatus === "Processing"
				? "Preparing video for playback…"
				: "Loading video…"}
		</div>
	);
};

/**
 * Mobile-width card rendering an announcement as the end-user sees it
 * in the app. Matches the sent/read-only view on EditAnnouncement but
 * renders content blocks structurally so inline image / video
 * references resolve through AzureImage / useAzureVideoDownload
 * instead of being embedded as raw (and therefore broken) `<img src>`
 * tags in body HTML.
 */
const AnnouncementPreview: FC<Props> = ({
	title,
	heroImageRef,
	heroImagePreviewUrl,
	blocks,
	author,
	dateLabel
}) => {
	const displayTitle = title?.trim() || "Announcement title";
	const authorName = author
		? `${author.firstName} ${author.lastName}`.trim()
		: null;
	const authorInitials = authorName
		? authorName
				.split(" ")
				.slice(0, 2)
				.map((n) => n[0])
				.join("")
				.toUpperCase()
		: "";

	const hasAnyBlockContent = blocks.some(
		(b) => b.content?.trim() || b.previewUrl
	);

	return (
		<div className="rounded-xl border border-border bg-white shadow-sm max-w-[390px] p-5">
			{dateLabel && (
				<div className="flex items-center gap-2 mb-2">
					<span className="text-[13px] text-muted-foreground">{dateLabel}</span>
				</div>
			)}

			<h2 className="text-xl font-bold text-foreground mb-3 break-words">
				{displayTitle}
			</h2>

			{authorName && (
				<div className="flex items-center gap-2.5 mb-4">
					<div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-medium">
						{authorInitials}
					</div>
					<span className="text-sm font-medium text-foreground">
						{authorName}
					</span>
				</div>
			)}

			{/* Hero image — respect uploaded aspect ratio, never crop.
			    w-full scales the image to the card's width; h-auto lets
			    the height follow the image's natural ratio. */}
			{heroImagePreviewUrl ? (
				<div className="rounded-lg overflow-hidden mb-4 -mx-1">
					<img src={heroImagePreviewUrl} alt="" className="w-full h-auto" />
				</div>
			) : heroImageRef ? (
				<div className="rounded-lg overflow-hidden mb-4 -mx-1">
					<AzureImage
						uuid={heroImageRef}
						imageProfile="admin-content-image"
						alt=""
						className="w-full h-auto"
					/>
				</div>
			) : null}

			{hasAnyBlockContent ? (
				<div className="flex flex-col gap-0">
					{blocks.map((block) => {
						if (block.type === "text") {
							if (!block.content?.trim()) return null;
							return <TextBlock key={block.id} content={block.content} />;
						}
						if (block.type === "image") {
							return <ImageBlockPreview key={block.id} block={block} />;
						}
						if (block.type === "video") {
							return <VideoBlockPreview key={block.id} block={block} />;
						}
						return null;
					})}
				</div>
			) : (
				<p className="text-sm text-muted-foreground italic">
					Start adding content to see it here.
				</p>
			)}
		</div>
	);
};

export { sanitiseTextBlock };
export default AnnouncementPreview;
